import {ActionContext, Module} from 'vuex'
import {AppState} from '@/stores'
import {getListOperation, GetOperation} from '@/service/operation/GetOperation'
import {LocalStore} from '@/service/store/LocalStore'
import {SiteManagerQuotationRequestBean} from '@/service/model/sitemanager/SiteManagerQuotationRequestBean'
import {
  CustomerQuotationResponsesViewBean,
  QuotationRequestApi,
  QuotationRequestValidationResultBean,
  SiteManagerValidateResponseResultBean,
} from '@/service/api/sitemanager/QuotationRequestApi'
import Result from '@/service/operation/Result'
import {I18n} from 'vue-i18n'
import {
  SiteManagerCreateQuotationRequestModule
} from '@/stores/modules/sitemanager/SiteManagerCreateQuotationRequestModule'
import {DeliveryNoteState} from '@/stores/modules/DeliveryNoteModule'
import {SiteManagerQuotationResponseApi} from '@/service/api/sitemanager/SiteManagerQuotationResponseApi'
import {SiteManagerQuotationResponseBean} from '@/service/model/sitemanager/SiteManagerQuotationResponseBean'
import {
  SiteManagerSelectQuotationResponseModule
} from '@/stores/modules/sitemanager/SiteManagerSelectQuotationResponseModule'
import {QuotationRequestV2Bean} from '@/service/model/sitemanager/QuotationRequestV2Bean'
import {QuotationRequestDetailV2Bean} from '@/service/model/sitemanager/QuotationRequestDetailV2Bean'
import {QuotationRequestV3Bean} from '@/service/model/sitemanager/QuotationRequestV3Bean'

const quotationRequestsStoreKey = 'quotationRequests'
const quotationRequestsV2StoreKey = 'quotationRequestsV2'
const quotationRequestsV3StoreKey = 'quotationRequestsV3'
const currentQuotationResponseStoreKey = 'currentQuotationResponseStoreKey'

export interface SiteManagerQuotationRequestModuleState {
  quotationRequestsOperation: GetOperation<Array<SiteManagerQuotationRequestBean>, string>
  quotationRequestsResult: Result<Array<SiteManagerQuotationRequestBean>>
  quotationRequestsV2Operation: GetOperation<Array<QuotationRequestV2Bean>, string> | undefined
  quotationRequestsV2Result: Result<Array<QuotationRequestV2Bean>> | undefined
  quotationRequestsV3Operation: GetOperation<Array<QuotationRequestV3Bean>, string> | undefined
  quotationRequestsV3Result: Result<Array<QuotationRequestV3Bean>> | undefined
  currentQuotationRequestResult: GetOperation<Array<SiteManagerQuotationRequestBean>, string>
  currentQuotationResponsesResult: GetOperation<Array<SiteManagerQuotationRequestBean>, string>
  currentQuotationRequestDetailResult: Result<QuotationRequestDetailV2Bean> | undefined
  currentQuotationResponsesV2Result: Result<CustomerQuotationResponsesViewBean> | undefined
}

export class SiteManagerQuotationRequestModule implements Module<SiteManagerQuotationRequestModuleState, AppState> {
  namespaced = true
  modules = {}
  actions = {
    fetchQuotationRequests: this.fetchQuotationRequestsAction.bind(this),
    refreshQuotationRequests: this.refreshQuotationRequestsAction.bind(this),
    fetchQuotationRequestsV2: this.fetchQuotationRequestsV2Action.bind(this),
    refreshQuotationRequestsV2: this.refreshQuotationRequestsV2Action.bind(this),
    fetchQuotationRequestsV3: this.fetchQuotationRequestsV3Action.bind(this),
    refreshQuotationRequestsV3: this.refreshQuotationRequestsV3Action.bind(this),
    validateResponse: this.validateResponseAction.bind(this),
    validateResponseV2: this.validateResponseV2Action.bind(this),
    cancelQuotationRequest: this.cancelQuotationRequestAction.bind(this),
    deleteQuotationRequest: this.deleteQuotationRequestAction.bind(this),

    selectQuotationRequest: this.selectQuotationRequestAction.bind(this),
 
    clearQuotationRequestModule: this.clearQuotationRequestModuleAction.bind(this),
  }
  mutations = {
    setQuotationRequestsOperation: (state, operation) => state.quotationRequestsOperation = operation,
    setQuotationRequestsResult: (state, result) => state.quotationRequestsResult = result,
    setQuotationRequestsV2Operation: (state, operation) => state.quotationRequestsV2Operation = operation,
    setQuotationRequestsV2Result: (state, result) => state.quotationRequestsV2Result = result,
    setQuotationRequestsV3Operation: (state, operation) => state.quotationRequestsV3Operation = operation,
    setQuotationRequestsV3Result: (state, result) => state.quotationRequestsV3Result = result,
    setCurrentQuotationRequestResult: (state, result) => state.currentQuotationRequestResult = result,
    setCurrentQuotationResponsesResult: (state, result) => state.currentQuotationResponsesResult = result,
    setCurrentQuotationRequestDetailResult: (state, result) => state.currentQuotationRequestDetailResult = result,
    setCurrentQuotationResponsesV2Result: (state, result) => state.currentQuotationResponsesV2Result = result,
  }
  getters = {
    quotationRequestsResult: (state) => state.quotationRequestsResult,
    quotationRequestsV2Result: (state) => state.quotationRequestsV2Result,
    quotationRequestsV3Result: (state) => state.quotationRequestsV3Result,
    currentQuotationRequestResult: (state) => state.currentQuotationRequestResult,
    currentQuotationResponsesResult: (state) => state.currentQuotationResponsesResult,
    currentQuotationRequestDetailResult: (state) => state.currentQuotationRequestDetailResult,
    currentQuotationResponsesV2Result: (state) => state.currentQuotationResponsesV2Result,
  }

  private readonly quotationRequestV2Store = new LocalStore<QuotationRequestV2Bean, string>({
    localStorageKey: 'site-manager-quotation-request-v2'
  })

  private readonly quotationRequestV3Store = new LocalStore<QuotationRequestV3Bean, string>({
    localStorageKey: 'site-manager-quotation-request-v3'
  })

  constructor(
    private readonly i18n: I18n,
    private readonly listStore: LocalStore<Array<string>, string>,
    private readonly quotationRequestStore: LocalStore<SiteManagerQuotationRequestBean, string>,
    private readonly quotationRequestApi: QuotationRequestApi,
    private readonly quotationResponseStore: LocalStore<SiteManagerQuotationResponseBean, string>,
    private readonly quotationResponseApi: SiteManagerQuotationResponseApi,
    createModule: SiteManagerCreateQuotationRequestModule,
    selectModule: SiteManagerSelectQuotationResponseModule,
  ) {
    this.modules = {
      create: createModule,
      select: selectModule
    }
  }

  private async fetchQuotationRequestsAction(
    context: ActionContext<SiteManagerQuotationRequestModuleState, AppState>
  ): Promise<Array<SiteManagerQuotationRequestBean> | null> {
    const operation = getListOperation(
      this.quotationRequestStore,
      this.listStore,
      quotationRequestsStoreKey,
      it => it.id,
      () => this.quotationRequestApi.fetchQuotationRequests()
    )

    context.commit('setQuotationRequestsOperation', operation)

    return await operation.get(
      {params: null},
      it => {
        context.commit('setQuotationRequestsResult', it)
      }
    )
  }

  async refreshQuotationRequestsAction(
    context: ActionContext<SiteManagerQuotationRequestModuleState, AppState>
  ): Promise<Array<SiteManagerQuotationRequestBean> | null> {
    const operation = context.state.quotationRequestsOperation
    if (!operation) {
      return this.fetchQuotationRequestsAction(context)
    }
    return operation.refresh(true)
  }

  private async fetchQuotationRequestsV2Action(
    context: ActionContext<SiteManagerQuotationRequestModuleState, AppState>
  ): Promise<Array<QuotationRequestV2Bean> | null> {
    const operation = getListOperation(
      this.quotationRequestV2Store,
      this.listStore,
      quotationRequestsV2StoreKey,
      it => it.siteId,
      () => this.quotationRequestApi.fetchQuotationRequestsV2()
    )

    context.commit('setQuotationRequestsV2Operation', operation)

    return await operation.get(
      {params: null},
      it => {
        context.commit('setQuotationRequestsV2Result', it)
      }
    )
  }

  async refreshQuotationRequestsV2Action(
    context: ActionContext<SiteManagerQuotationRequestModuleState, AppState>
  ): Promise<Array<QuotationRequestV2Bean> | null> {
    const operation = context.state.quotationRequestsV2Operation
    if (!operation) {
      return this.fetchQuotationRequestsV2Action(context)
    }
    return operation.refresh(true)
  }

  private async fetchQuotationRequestsV3Action(
    context: ActionContext<SiteManagerQuotationRequestModuleState, AppState>
  ): Promise<Array<QuotationRequestV3Bean> | null> {
    const operation = getListOperation(
      this.quotationRequestV3Store,
      this.listStore,
      quotationRequestsV3StoreKey,
      it => it.siteId,
      () => this.quotationRequestApi.fetchQuotationRequestsV3()
    )

    context.commit('setQuotationRequestsV3Operation', operation)

    return await operation.get(
      {params: null},
      it => {
        context.commit('setQuotationRequestsV3Result', it)
      }
    )
  }

  async refreshQuotationRequestsV3Action(
    context: ActionContext<SiteManagerQuotationRequestModuleState, AppState>
  ): Promise<Array<QuotationRequestV3Bean> | null> {
    const operation = context.state.quotationRequestsV3Operation
    if (!operation) {
      return this.fetchQuotationRequestsV3Action(context)
    }
    return operation.refresh(true)
  }

  async validateResponseAction(
    _context: ActionContext<SiteManagerQuotationRequestModuleState, AppState>,
    payload: { requestId: string; responseId: string }
  ): Promise<QuotationRequestValidationResultBean> {
    return this.quotationRequestApi.validateResponse(payload.requestId, payload.responseId)
  }

  async validateResponseV2Action(
    _context: ActionContext<SiteManagerQuotationRequestModuleState, AppState>,
    payload: { requestId: string; responseId: string }
  ): Promise<SiteManagerValidateResponseResultBean> {
    return this.quotationRequestApi.validateResponseV2(payload.requestId, payload.responseId)
  }

  async cancelQuotationRequestAction(
    _context: ActionContext<SiteManagerQuotationRequestModuleState, AppState>,
    requestId: string
  ): Promise<void> {
    return this.quotationRequestApi.cancelQuotationRequestV2(requestId)
  }

  async deleteQuotationRequestAction(
    _context: ActionContext<SiteManagerQuotationRequestModuleState, AppState>,
    requestId: string
  ): Promise<void> {
    return this.quotationRequestApi.deleteQuotationRequest(requestId)
  }
 
  private async selectQuotationRequestAction(
    context: ActionContext<DeliveryNoteState, AppState>,
    requestId: string
  ): Promise<any> {
    context.commit('setCurrentQuotationRequestResult', undefined)
    context.commit('setCurrentQuotationResponsesResult', undefined)
    context.commit('setCurrentQuotationRequestDetailResult', undefined)
    context.commit('setCurrentQuotationResponsesV2Result', undefined)

    const detailPromise = this.quotationRequestApi.fetchQuotationRequestV2(requestId)
      .then(detail => context.commit('setCurrentQuotationRequestDetailResult', new Result(undefined, detail)))
      .catch(err => context.commit('setCurrentQuotationRequestDetailResult', new Result(undefined, undefined, err)))

    const responsesPromise = this.quotationRequestApi.fetchQuotationRequestResponsesV2(requestId)
      .then(r => context.commit('setCurrentQuotationResponsesV2Result', new Result(undefined, r)))
      .catch(err => context.commit('setCurrentQuotationResponsesV2Result', new Result(undefined, undefined, err)))

    return Promise.all([detailPromise, responsesPromise])
  }

  clearQuotationRequestModuleAction(
    context: ActionContext<SiteManagerQuotationRequestModuleState, AppState>
  ) {
    context.commit('setQuotationRequestsResult', undefined)
    context.commit('setQuotationRequestsOperation', undefined)
    context.commit('setQuotationRequestsV2Result', undefined)
    context.commit('setQuotationRequestsV2Operation', undefined)
    context.commit('setQuotationRequestsV3Result', undefined)
    context.commit('setQuotationRequestsV3Operation', undefined)
    context.commit('setCurrentQuotationRequestResult', undefined)
    context.commit('setCurrentQuotationResponsesResult', undefined)
    context.commit('setCurrentQuotationRequestDetailResult', undefined)
    this.listStore.deleteItem(quotationRequestsStoreKey)
    this.listStore.deleteItem(quotationRequestsV2StoreKey)
    this.listStore.deleteItem(quotationRequestsV3StoreKey)
    this.listStore.deleteItem(currentQuotationResponseStoreKey)
    this.quotationRequestStore.clear()
    this.quotationRequestV2Store.clear()
    this.quotationRequestV3Store.clear()
    this.quotationResponseStore.clear()
  }
}
